/**
 * @fileoverview Unified sales-roster API calls.
 * @description Wraps GET /sales/sales-users, GET /sales/sales-users/:id (the shared "View User"
 * profile for both User Management and Sales Team), POST /sales/sales-users (create), PATCH
 * /sales/sales-users/:id (update). Deactivating and reactivating a user is User Management's own
 * flow (POST /users/{userId}/deactivate and /reactivate, in features/users). A 422 from any mutation here
 * carries its business-rule message as the rejected error's own `.message` (see
 * response.interceptor.ts's buildFailureException) — callers read it the same way
 * the target forms already do: `error instanceof Error ?
 * error.message : String(error)`.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { httpClient } from '@/shared/api/http-client'

import type {
  SalesUserProfile,
  UpdateSalesUserInput,
} from '@/features/sales/types/sales-roster.types'
import type { SalesUserRecord } from '@/features/sales/types/tenant.types'

export interface SalesRosterFilters {
  role?: SalesUserRecord['role']
  status?: SalesUserRecord['status']
  reportsTo?: string
}

export interface UpdateSalesUserRequest extends UpdateSalesUserInput {
  actingUserId: string
  actingUserRole: string
}

/**
 * @description Fetches the sales roster matching the supplied filters.
 * @param {SalesRosterFilters} [filters] - The filters to apply.
 * @returns {Promise<SalesUserRecord[]>} Every matching sales user.
 */
export async function getSalesRoster(filters: SalesRosterFilters = {}): Promise<SalesUserRecord[]> {
  const { data } = await httpClient.get<SalesUserRecord[]>('/sales/sales-users', {
    params: filters,
  })
  return data
}

/**
 * @description Fetches one sales user's shared "View User" profile, including their current
 * assignments.
 * @param {string} id - The sales user id to fetch.
 * @returns {Promise<SalesUserProfile>} The user's profile plus their assigned tenant ids.
 */
export async function getSalesUserProfile(id: string): Promise<SalesUserProfile> {
  const { data } = await httpClient.get<SalesUserProfile>(`/sales/sales-users/${id}`)
  return data
}

/**
 * @description Updates a sales user's editable profile and reporting details.
 * @param {string} id - The sales user id to update.
 * @param {UpdateSalesUserRequest} input - The fields to change and the acting administrator.
 * @returns {Promise<SalesUserRecord>} The updated user.
 */
export async function updateSalesUser(
  id: string,
  input: UpdateSalesUserRequest,
): Promise<SalesUserRecord> {
  const { data } = await httpClient.patch<SalesUserRecord>(`/sales/sales-users/${id}`, input)
  return data
}
